import { createTheme, alpha } from '@mui/material/styles';

const ink = '#0F172A';
const muted = '#64748B';
const teal = '#0F766E';
const tealLight = '#14B8A6';
const tealDark = '#115E59';
const amber = '#D97706';
const wash = '#F1F5F9';

export const AppTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: teal,
      light: tealLight,
      dark: tealDark,
      contrastText: '#fff',
    },
    secondary: {
      main: amber,
      light: '#F59E0B',
      dark: '#B45309',
      contrastText: '#fff',
    },
    background: {
      default: wash,
      paper: '#FFFFFF',
    },
    text: {
      primary: ink,
      secondary: muted,
    },
    success: {
      main: '#15803D',
      light: '#22C55E',
      dark: '#166534',
    },
    error: {
      main: '#DC2626',
      light: '#F87171',
      dark: '#B91C1C',
    },
    warning: {
      main: amber,
      light: '#FBBF24',
      dark: '#B45309',
    },
    info: {
      main: '#0369A1',
      light: '#38BDF8',
      dark: '#075985',
    },
    divider: alpha(ink, 0.08),
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Fraunces", "Georgia", serif',
      fontWeight: 650,
      fontSize: '2.5rem',
      letterSpacing: '-0.02em',
      color: ink,
    },
    h2: {
      fontFamily: '"Fraunces", "Georgia", serif',
      fontWeight: 650,
      fontSize: '2rem',
      letterSpacing: '-0.02em',
      color: ink,
    },
    h3: {
      fontFamily: '"Fraunces", "Georgia", serif',
      fontWeight: 600,
      fontSize: '1.75rem',
      letterSpacing: '-0.015em',
      color: ink,
    },
    h4: {
      fontFamily: '"Fraunces", "Georgia", serif',
      fontWeight: 600,
      fontSize: '1.5rem',
      letterSpacing: '-0.01em',
      color: ink,
    },
    h5: {
      fontFamily: '"Fraunces", "Georgia", serif',
      fontWeight: 600,
      fontSize: '1.25rem',
      color: ink,
    },
    h6: {
      fontFamily: '"Plus Jakarta Sans", "Helvetica", "Arial", sans-serif',
      fontWeight: 650,
      fontSize: '1.05rem',
      color: ink,
    },
    subtitle1: {
      fontWeight: 600,
      fontSize: '1rem',
    },
    subtitle2: {
      fontWeight: 600,
      fontSize: '0.875rem',
    },
    body1: {
      fontSize: '0.95rem',
      lineHeight: 1.55,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.01em',
    },
    caption: {
      fontSize: '0.75rem',
      color: muted,
    },
    overline: {
      fontSize: '0.68rem',
      fontWeight: 700,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: muted,
    },
  },
  shape: {
    borderRadius: 10,
  },
  spacing: 8,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundImage:
            'radial-gradient(ellipse 80% 50% at 0% -20%, rgba(15, 118, 110, 0.07), transparent), radial-gradient(ellipse 60% 40% at 100% 0%, rgba(217, 119, 6, 0.05), transparent)',
          backgroundAttachment: 'fixed',
        },
        '@keyframes pageFadeIn': {
          from: { opacity: 0, transform: 'translateY(6px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '@keyframes metricLift': {
          from: { opacity: 0.7, transform: 'translateY(4px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
          '&:hover': {
            transform: 'none',
          },
        },
        containedPrimary: {
          backgroundColor: teal,
          '&:hover': {
            backgroundColor: tealDark,
          },
        },
        outlined: {
          borderWidth: 1.5,
          '&:hover': {
            borderWidth: 1.5,
            backgroundColor: alpha(teal, 0.06),
          },
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none',
          border: `1px solid ${alpha(ink, 0.08)}`,
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
        },
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${alpha(ink, 0.08)}`,
          boxShadow: 'none',
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'none',
            boxShadow: '0 4px 16px rgba(15, 23, 42, 0.06)',
            borderColor: alpha(teal, 0.25),
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          marginBottom: 2,
          transition: 'background-color 0.15s ease',
          '&:hover': {
            transform: 'none',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: ink,
          boxShadow: 'none',
          borderBottom: `1px solid ${alpha('#fff', 0.08)}`,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 600,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#fff',
            '&:hover fieldset': {
              borderColor: teal,
            },
            '&.Mui-focused fieldset': {
              borderWidth: 1.5,
              borderColor: teal,
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
          boxShadow: '0 24px 48px rgba(15, 23, 42, 0.16)',
          border: `1px solid ${alpha(ink, 0.08)}`,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: muted,
            backgroundColor: alpha(wash, 0.9),
            borderBottom: `1px solid ${alpha(ink, 0.08)}`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${alpha(ink, 0.06)}`,
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          backgroundColor: teal,
          boxShadow: '0 4px 14px rgba(15, 118, 110, 0.35)',
          '&:hover': {
            backgroundColor: tealDark,
            transform: 'none',
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: '10px !important',
          border: `1px solid ${alpha(ink, 0.08)}`,
          boxShadow: 'none',
          '&:before': {
            display: 'none',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontWeight: 500,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 6,
          backgroundColor: alpha(teal, 0.12),
        },
        bar: {
          borderRadius: 4,
          backgroundColor: teal,
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          '&.Mui-selected': {
            backgroundColor: teal,
            color: 'white',
            '&:hover': {
              backgroundColor: tealDark,
            },
          },
        },
      },
    },
  },
});

export default AppTheme;
